import axios from "axios";
import { useParams } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import useFetch from "../../clients/features/get_withcredentials";

const normalize = (date) => {
  const d = new Date(date);
  d.setUTCHours(0, 0, 0, 0);
  return d;
};

const Bills = () => {
  const { hotelId } = useParams();

  const { data = [], refreshData } = useFetch(
    `https://api.komorotel.com/api/reservation/hotel/${hotelId}`
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cancellationRequests, setCancellationRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");



  const getStatus = (status) => {
    switch (status) {
      case "confirmed": return { label: "Confirmée", bg: "bg-green-50", text: "text-green-700", dot: "bg-green-500" };
      case "pending": return { label: "En attente", bg: "bg-yellow-50", text: "text-yellow-700", dot: "bg-yellow-500" };
      case "cancelled": return { label: "Annulée", bg: "bg-red-50", text: "text-red-700", dot: "bg-red-500" };
      case "pending_cancel": return { label: "Annulation demandée", bg: "bg-orange-50", text: "text-orange-700", dot: "bg-orange-500" };
      case "no_show": return { label: "No-show", bg: "bg-gray-100", text: "text-gray-600", dot: "bg-gray-400" };
      default: return { label: "Inconnu", bg: "bg-gray-100", text: "text-gray-500", dot: "bg-gray-300" };
    }
  };

  useEffect(() => {
    const fetchCancellations = async () => {
      try {
        const res = await axios.get(
          `https://api.komorotel.com/api/reservation/hotel/${hotelId}/pending-cancellations`,
          { withCredentials: true }
        );
        setCancellationRequests(res.data);
      } catch (err) {
        setError(err.message);
      }
    };
    fetchCancellations();
  }, [hotelId]);

  const calculateNights = (checkIn, checkOut) => {
    return Math.ceil((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24));
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const processedReservations = useMemo(() => {
    let filtered = data.filter((r) => {
      const text = search.toLowerCase();
      const matchSearch =
        r.guest?.firstName?.toLowerCase().includes(text) ||
        r.guest?.lastName?.toLowerCase().includes(text) ||
        r.roomId?.title?.toLowerCase().includes(text) ||
        r._id?.toLowerCase().includes(text);
      const matchStatus = statusFilter === "all" ? true :
        statusFilter === "past"
          ? (() => {
              const today = normalize(new Date());
              const checkOut = normalize(r.checkOutDate);
              return r.status === "confirmed" && checkOut < today;
            })()
          : r.status === statusFilter;
            return matchSearch && matchStatus;
          });

    filtered.sort((a, b) => {
      let vA, vB;
      if (sortField === "guest") { vA = a.guest?.lastName || ""; vB = b.guest?.lastName || ""; }
      if (sortField === "createdAt") { vA = new Date(a.createdAt); vB = new Date(b.createdAt); }
      if (sortField === "price") { vA = a.totalPrice; vB = b.totalPrice; }
      if (sortField === "room") { vA = a.roomId?.title || ""; vB = b.roomId?.title || ""; }
      if (vA < vB) return sortDirection === "asc" ? -1 : 1;
      if (vA > vB) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [data, search, sortField, sortDirection, statusFilter]);

  const stats = useMemo(() => ({
    all: data.length,
    confirmed: data.filter(r => r.status === "confirmed").length,
    past: data.filter(r => {
      const today = normalize(new Date());
      const checkOut = normalize(r.checkOutDate);
      return r.status === "confirmed" && checkOut < today;
    }).length,
    pending_cancel: data.filter(r => r.status === "pending_cancel").length,
    cancelled: data.filter(r => r.status === "cancelled").length,
    no_show: data.filter(r => r.status === "no_show").length,
  }), [data]);

  const handleNoShow = async (reservationId) => {
    try {
      setLoading(true);
      await axios.put(
        `https://api.komorotel.com/api/reservation/no-show/${reservationId}`,
        {},
        { withCredentials: true }
      );
    } catch (err) {
      console.error(err);
    } finally {
      refreshData();
      setLoading(false);
    }
  };

  const handleDeleteReservation = async (requestId) => {
    try {
      setLoading(true);
      await axios.put(
        `https://api.komorotel.com/api/reservation/cancellation-requests/${requestId}`,
        { status: "approved" },
        { withCredentials: true }
      );
    } catch (err) {
      console.error(err);
    } finally {
      refreshData();
      setLoading(false);
    }
  };

  const SortIcon = ({ field }) => (
    <span className="ml-1 text-gray-300">
      {sortField === field ? (sortDirection === "asc" ? "↑" : "↓") : "↕"}
    </span>
  );

  const filters = [
    { key: "all", label: "Toutes", count: stats.all, color: "bg-gray-900 text-white" },
    { key: "confirmed", label: "Confirmées", count: stats.confirmed, color: "bg-green-600 text-white" },
    { key: "past", label: "Passées", count: stats.past, color: "bg-gray-500 text-white" },    
    { key: "pending_cancel", label: "Annulations", count: stats.pending_cancel, color: "bg-orange-500 text-white" },
    { key: "cancelled", label: "Annulées", count: stats.cancelled, color: "bg-red-500 text-white" },
    { key: "no_show", label: "No-show", count: stats.no_show, color: "bg-gray-500 text-white" },
  ];

  return (
    // ❌ enlève le max-w
<div className="w-full">

      {/* HEADER */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Réservations</h1>
        <p className="text-sm text-gray-400 mt-1">Gérez les réservations de votre hôtel</p>
      </div>

      {/* STATS + FILTRES */}
      <div className="flex flex-wrap gap-2 mb-6">
        {filters.map(f => (
          <button
            key={f.key}
            onClick={() => setStatusFilter(f.key)}
            className={`
              px-4 py-2 rounded-xl text-sm font-semibold transition-all
              ${statusFilter === f.key
                ? f.color
                : "bg-white border border-gray-200 text-gray-600 hover:border-gray-300"
              }
            `}
          >
            {f.label} <span className="ml-1 opacity-75">({f.count})</span>
          </button>
        ))}
      </div>

      {/* SEARCH */}
      <div className="mb-5">
        <input
          type="text"
          placeholder="Rechercher client, chambre ou ID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-md px-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#3A9D23] transition"
        />
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">ID</th>
                <th
                  className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-800"
                  onClick={() => toggleSort("guest")}
                >
                  Client <SortIcon field="guest" />
                </th>
                <th
                  className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-800"
                  onClick={() => toggleSort("createdAt")}
                >
                  Dates <SortIcon field="createdAt" />
                </th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Nuits</th>
                <th
                  className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-800"
                  onClick={() => toggleSort("room")}
                >
                  Chambre <SortIcon field="room" />
                </th>
                <th
                  className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-800"
                  onClick={() => toggleSort("price")}
                >
                  Prix <SortIcon field="price" />
                </th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Statut</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {processedReservations.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-gray-400">
                    Aucune réservation trouvée
                  </td>
                </tr>
              )}

              {processedReservations.map((reservation) => {
                const today = normalize(new Date());
                const checkIn = normalize(reservation.checkInDate);
                const checkOut = normalize(reservation.checkOutDate);
                const isUpcoming = today < checkIn;
                const isOngoing = today >= checkIn && today < checkOut;
                const isPast = today >= checkOut;

                const cancellationRequest = cancellationRequests?.find(
                  r => String(r.reservationId?._id || r.reservationId) === String(reservation._id)
                );

                const displayId = reservation?.reservationId || reservation._id.slice(0, 6).toUpperCase();
                const status = getStatus(reservation.status);

                return (
                  <tr
                    key={reservation._id}
                    className={`
                      hover:bg-gray-50 transition
                      ${reservation.status === "cancelled" ? "opacity-60" : ""}
                    `}
                  >
                    {/* ID */}
                    <td className="px-5 py-4">
                      <span className="font-mono text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-lg">
                        {displayId}
                      </span>
                    </td>

                    {/* CLIENT */}
                    <td className="px-5 py-4">
                      <p className="font-semibold text-gray-900">
                        {reservation.guest?.firstName} {reservation.guest?.lastName}
                      </p>
                      {reservation.guest?.email && (
                        <p className="text-xs text-gray-400 mt-0.5">{reservation.guest.email}</p>
                      )}
                    </td>

                    {/* DATES */}
                    <td className="px-5 py-4">
                      <p className="text-gray-800">
                        {new Date(reservation.checkInDate).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
                        {" → "}
                        {new Date(reservation.checkOutDate).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
                      </p>
                      {reservation.status === "confirmed" && (
                        <p className={`text-xs mt-0.5 ${isUpcoming ? "text-blue-500" : isOngoing ? "text-green-500" : "text-gray-400"}`}>
                          {isUpcoming && "À venir"}
                          {isOngoing && "En cours"}
                          {isPast && "Terminé"}
                        </p>
                      )}
                    </td>

                    {/* NUITS */}
                    <td className="px-5 py-4 text-gray-600">
                      {calculateNights(reservation.checkInDate, reservation.checkOutDate)}n
                    </td>

                    {/* CHAMBRE */}
                    <td className="px-5 py-4 text-gray-700">
                      {reservation.roomId?.title || "—"}
                    </td>

                    {/* PRIX */}
                    <td className="px-5 py-4">
                      <span className="font-bold text-gray-900">{reservation.totalPrice} €</span>
                    </td>

                    {/* STATUT */}
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${status.bg} ${status.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                        {status.label}
                      </span>
                    </td>

                    {/* ACTIONS */}
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">

                        {/* NO SHOW */}
                        {reservation.status === "confirmed" && isPast && (
                          <button
                            onClick={() => handleNoShow(reservation._id)}
                            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
                          >
                            No-show
                          </button>
                        )}

                        {/* CONFIRMER ANNULATION */}
                        {reservation.status === "pending_cancel" && cancellationRequest && (
                          <button
                            onClick={() => handleDeleteReservation(cancellationRequest._id)}
                            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition"
                          >
                            Confirmer annulation
                          </button>
                        )}

                        {reservation.status === "cancelled" && (
                          <span className="text-xs text-red-400">✔ Annulée</span>
                        )}

                        {!["cancelled", "pending_cancel", "no_show"].includes(reservation.status) && !isPast && (
                          <span className="text-xs text-gray-300">—</span>
                        )}

                      </div>
                    </td>

                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default Bills;